import { startOfMonth, format } from 'date-fns';
import { swedishToday } from './calendarDates';
import {
  BrandSortBy,
  ProductSortBy,
  SortOrder,
  SupplierSortBy,
  SupplierStatsGranularity,
} from '../types/supplierStats';

export interface BrandListFilterState {
  startDate: Date;
  endDate: Date;
  preset: string;
  search?: string;
  page: number;
  sortBy: BrandSortBy;
  sortOrder: SortOrder;
}

export interface BrandProductsFilterState {
  startDate: Date;
  endDate: Date;
  preset: string;
  search?: string;
  page: number;
  sortBy: ProductSortBy;
  sortOrder: SortOrder;
  granularity: SupplierStatsGranularity;
  categoryId: number | null;
}

export interface SupplierStatsListFilterState {
  startDate: Date;
  endDate: Date;
  preset: string;
  search?: string;
  page: number;
  sortBy: SupplierSortBy;
  sortOrder: SortOrder;
}

export interface SupplierBrandListFilterState {
  startDate: Date;
  endDate: Date;
  preset: string;
  search?: string;
  page: number;
  sortBy: BrandSortBy;
  sortOrder: SortOrder;
}

export interface SupplierStatsDashboardFilterState {
  startDate: Date;
  endDate: Date;
  preset: string;
  granularity: SupplierStatsGranularity;
}

export interface ProductDetailFilterState {
  startDate: Date;
  endDate: Date;
  preset: string;
}

export interface PurchaseOrderStatsFilterState {
  bucket: 'draft' | 'done' | 'all';
  season: string | null;
  supplierId: number | null;
  deliveryMonthFrom: string | null;
  deliveryMonthTo: string | null;
  focusMonth: string | null; // YYYY-MM
}

export interface PurchaseOrderListFilterState {
  status: string | null;
  viskanStatus: string | null;
  ready: 'all' | 'ready' | 'not_ready';
  season: string | null;
  supplierId: number | null;
}

const parseDate = (value: string | null, fallback: Date): Date => {
  if (!value) return fallback;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return fallback;
  const d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (format(d, 'yyyy-MM-dd') !== value) return fallback;
  return Number.isNaN(d.getTime()) ? fallback : d;
};

const parseNumber = (value: string | null, fallback: number): number => {
  if (!value) return fallback;
  const num = Number(value);
  return Number.isNaN(num) || num <= 0 ? fallback : num;
};

const parseMonth = (value: string | null): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  return /^\d{4}-\d{2}$/.test(trimmed) ? trimmed : null;
};

const parseSortOrder = (
  value: string | null,
  fallback: SortOrder,
): SortOrder => {
  return value === 'ASC' || value === 'DESC' ? value : fallback;
};

const BRAND_SORT_OPTIONS: BrandSortBy[] = [
  'revenueChange',
  'unitsChange',
  'revenue',
  'units',
  'retailUnits',
  'onlineUnits',
  'retailRevenue',
  'onlineRevenue',
  'name',
  'stock',
  'products',
];

const SUPPLIER_SORT_OPTIONS: SupplierSortBy[] = [
  'revenueChange',
  'unitsChange',
  'revenue',
  'units',
  'retailUnits',
  'onlineUnits',
  'retailRevenue',
  'onlineRevenue',
  'name',
  'stock',
  'brands',
  'products',
];

const PRODUCT_SORT_OPTIONS: ProductSortBy[] = [
  'revenueChange',
  'unitsChange',
  'revenue',
  'units',
  'retailUnits',
  'onlineUnits',
  'retailRevenue',
  'onlineRevenue',
  'stock',
  'productNumber',
];

const parseGranularity = (
  value: string | null,
  fallback: SupplierStatsGranularity,
): SupplierStatsGranularity => {
  return value === 'product' ? 'product' : fallback;
};

const parseOptionalCategoryId = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const readSupplierStatsDashboardFilters = (
  params: URLSearchParams,
): SupplierStatsDashboardFilterState => {
  const today = swedishToday();
  const defaultStart = startOfMonth(today);
  const defaultEnd = today;

  const startDate = parseDate(params.get('startDate'), defaultStart);
  const endDate = parseDate(params.get('endDate'), defaultEnd);
  const preset =
    params.get('preset') ||
    (params.get('startDate') ? 'Anpassad period' : 'Denna månad');
  const granularity = parseGranularity(params.get('granularity'), 'color');

  return {
    startDate,
    endDate,
    preset,
    granularity,
  };
};

export const writeSupplierStatsDashboardFilters = (
  baseParams: URLSearchParams,
  state: Partial<SupplierStatsDashboardFilterState>,
): URLSearchParams => {
  const params = new URLSearchParams(baseParams.toString());

  if (state.startDate) {
    params.set('startDate', format(state.startDate, 'yyyy-MM-dd'));
  }
  if (state.endDate) {
    params.set('endDate', format(state.endDate, 'yyyy-MM-dd'));
  }
  if (state.preset) {
    params.set('preset', state.preset);
  }
  if (state.granularity) {
    params.set('granularity', state.granularity);
  }

  return params;
};

export const readBrandListFilters = (
  params: URLSearchParams,
): BrandListFilterState => {
  const today = swedishToday();
  const defaultStart = startOfMonth(today);
  const defaultEnd = today;

  const startDate = parseDate(params.get('startDate'), defaultStart);
  const endDate = parseDate(params.get('endDate'), defaultEnd);
  const preset =
    params.get('preset') ||
    (params.get('startDate') ? 'Anpassad period' : 'Denna månad');

  const search = params.get('search') || undefined;
  const page = parseNumber(params.get('page'), 1);

  const sortByParam = params.get('sortBy') as BrandSortBy | null;
  const sortBy: BrandSortBy =
    sortByParam && BRAND_SORT_OPTIONS.includes(sortByParam)
      ? sortByParam
      : 'units';

  const sortOrder = parseSortOrder(params.get('sortOrder'), 'DESC');

  return {
    startDate,
    endDate,
    preset,
    search,
    page,
    sortBy,
    sortOrder,
  };
};

export const writeBrandListFilters = (
  baseParams: URLSearchParams,
  state: Partial<BrandListFilterState>,
): URLSearchParams => {
  const params = new URLSearchParams(baseParams.toString());

  if (state.startDate) {
    params.set('startDate', format(state.startDate, 'yyyy-MM-dd'));
  }
  if (state.endDate) {
    params.set('endDate', format(state.endDate, 'yyyy-MM-dd'));
  }
  if (state.preset) {
    params.set('preset', state.preset);
  }
  if ('search' in state) {
    if (state.search) {
      params.set('search', state.search);
    } else {
      params.delete('search');
    }
  }
  if (state.page !== undefined) {
    params.set('page', String(state.page));
  }
  if (state.sortBy) {
    params.set('sortBy', state.sortBy);
  }
  if (state.sortOrder) {
    params.set('sortOrder', state.sortOrder);
  }

  return params;
};

export const readSupplierStatsListFilters = (
  params: URLSearchParams,
): SupplierStatsListFilterState => {
  const today = swedishToday();
  const defaultStart = startOfMonth(today);
  const defaultEnd = today;

  const startDate = parseDate(params.get('startDate'), defaultStart);
  const endDate = parseDate(params.get('endDate'), defaultEnd);
  const preset =
    params.get('preset') ||
    (params.get('startDate') ? 'Anpassad period' : 'Denna månad');

  const search = params.get('search') || undefined;
  const page = parseNumber(params.get('page'), 1);

  const sortByParam = params.get('sortBy') as SupplierSortBy | null;
  const sortBy: SupplierSortBy =
    sortByParam && SUPPLIER_SORT_OPTIONS.includes(sortByParam)
      ? sortByParam
      : 'units';

  const sortOrder = parseSortOrder(params.get('sortOrder'), 'DESC');

  return {
    startDate,
    endDate,
    preset,
    search,
    page,
    sortBy,
    sortOrder,
  };
};

export const writeSupplierStatsListFilters = (
  baseParams: URLSearchParams,
  state: Partial<SupplierStatsListFilterState>,
): URLSearchParams => {
  const params = new URLSearchParams(baseParams.toString());

  if (state.startDate) {
    params.set('startDate', format(state.startDate, 'yyyy-MM-dd'));
  }
  if (state.endDate) {
    params.set('endDate', format(state.endDate, 'yyyy-MM-dd'));
  }
  if (state.preset) {
    params.set('preset', state.preset);
  }
  if ('search' in state) {
    if (state.search) {
      params.set('search', state.search);
    } else {
      params.delete('search');
    }
  }
  if (state.page !== undefined) {
    params.set('page', String(state.page));
  }
  if (state.sortBy) {
    params.set('sortBy', state.sortBy);
  }
  if (state.sortOrder) {
    params.set('sortOrder', state.sortOrder);
  }

  return params;
};

export const readSupplierBrandListFilters = (
  params: URLSearchParams,
): SupplierBrandListFilterState => {
  const today = swedishToday();
  const defaultStart = startOfMonth(today);
  const defaultEnd = today;

  const startDate = parseDate(params.get('startDate'), defaultStart);
  const endDate = parseDate(params.get('endDate'), defaultEnd);
  const preset =
    params.get('preset') ||
    (params.get('startDate') ? 'Anpassad period' : 'Denna månad');

  const search = params.get('search') || undefined;
  const page = parseNumber(params.get('page'), 1);

  const sortByParam = params.get('sortBy') as BrandSortBy | null;
  const sortBy: BrandSortBy =
    sortByParam && BRAND_SORT_OPTIONS.includes(sortByParam)
      ? sortByParam
      : 'units';

  const sortOrder = parseSortOrder(params.get('sortOrder'), 'DESC');

  return {
    startDate,
    endDate,
    preset,
    search,
    page,
    sortBy,
    sortOrder,
  };
};

export const writeSupplierBrandListFilters = (
  baseParams: URLSearchParams,
  state: Partial<SupplierBrandListFilterState>,
): URLSearchParams => {
  const params = new URLSearchParams(baseParams.toString());

  if (state.startDate) {
    params.set('startDate', format(state.startDate, 'yyyy-MM-dd'));
  }
  if (state.endDate) {
    params.set('endDate', format(state.endDate, 'yyyy-MM-dd'));
  }
  if (state.preset) {
    params.set('preset', state.preset);
  }
  if ('search' in state) {
    if (state.search) {
      params.set('search', state.search);
    } else {
      params.delete('search');
    }
  }
  if (state.page !== undefined) {
    params.set('page', String(state.page));
  }
  if (state.sortBy) {
    params.set('sortBy', state.sortBy);
  }
  if (state.sortOrder) {
    params.set('sortOrder', state.sortOrder);
  }

  return params;
};

export const readBrandProductsFilters = (
  params: URLSearchParams,
): BrandProductsFilterState => {
  const today = swedishToday();
  const defaultStart = startOfMonth(today);
  const defaultEnd = today;

  const startDate = parseDate(params.get('startDate'), defaultStart);
  const endDate = parseDate(params.get('endDate'), defaultEnd);
  const preset =
    params.get('preset') ||
    (params.get('startDate') ? 'Anpassad period' : 'Denna månad');

  const search = params.get('search') || undefined;
  const page = parseNumber(params.get('page'), 1);

  const sortByParam = params.get('sortBy') as ProductSortBy | null;
  const sortBy: ProductSortBy =
    sortByParam && PRODUCT_SORT_OPTIONS.includes(sortByParam)
      ? sortByParam
      : 'units';

  const sortOrder = parseSortOrder(params.get('sortOrder'), 'DESC');
  const granularity = parseGranularity(params.get('granularity'), 'color');
  const categoryId = parseOptionalCategoryId(params.get('categoryId'));

  return {
    startDate,
    endDate,
    preset,
    search,
    page,
    sortBy,
    sortOrder,
    granularity,
    categoryId,
  };
};

export const writeBrandProductsFilters = (
  baseParams: URLSearchParams,
  state: Partial<BrandProductsFilterState>,
): URLSearchParams => {
  const params = new URLSearchParams(baseParams.toString());

  if (state.startDate) {
    params.set('startDate', format(state.startDate, 'yyyy-MM-dd'));
  }
  if (state.endDate) {
    params.set('endDate', format(state.endDate, 'yyyy-MM-dd'));
  }
  if (state.preset) {
    params.set('preset', state.preset);
  }
  if ('search' in state) {
    if (state.search) {
      params.set('search', state.search);
    } else {
      params.delete('search');
    }
  }
  if (state.page !== undefined) {
    params.set('page', String(state.page));
  }
  if (state.sortBy) {
    params.set('sortBy', state.sortBy);
  }
  if (state.sortOrder) {
    params.set('sortOrder', state.sortOrder);
  }
  if (state.granularity) {
    params.set('granularity', state.granularity);
  }
  if ('categoryId' in state) {
    if (state.categoryId) {
      params.set('categoryId', String(state.categoryId));
    } else {
      params.delete('categoryId');
    }
  }

  return params;
};

export const readProductDetailFilters = (
  params: URLSearchParams,
): ProductDetailFilterState => {
  const today = swedishToday();
  const defaultStart = startOfMonth(today);
  const defaultEnd = today;

  const startDate = parseDate(params.get('startDate'), defaultStart);
  const endDate = parseDate(params.get('endDate'), defaultEnd);
  const preset =
    params.get('preset') ||
    (params.get('startDate') ? 'Anpassad period' : 'Denna månad');

  return {
    startDate,
    endDate,
    preset,
  };
};

export const writeProductDetailFilters = (
  baseParams: URLSearchParams,
  state: Partial<ProductDetailFilterState>,
): URLSearchParams => {
  const params = new URLSearchParams(baseParams.toString());

  if (state.startDate) {
    params.set('startDate', format(state.startDate, 'yyyy-MM-dd'));
  }
  if (state.endDate) {
    params.set('endDate', format(state.endDate, 'yyyy-MM-dd'));
  }
  if (state.preset) {
    params.set('preset', state.preset);
  }

  return params;
};

export const readPurchaseOrderStatsFilters = (
  params: URLSearchParams,
): PurchaseOrderStatsFilterState => {
  const bucketParam = params.get('bucket');
  const bucket =
    bucketParam === 'draft' || bucketParam === 'done' || bucketParam === 'all'
      ? bucketParam
      : 'all';

  const season = (params.get('season') || '').trim() || null;

  const supplierIdRaw = params.get('supplierId');
  const supplierIdNum = supplierIdRaw != null ? Number(supplierIdRaw) : NaN;
  const supplierId =
    Number.isFinite(supplierIdNum) && supplierIdNum > 0 ? supplierIdNum : null;

  const deliveryMonthFrom = parseMonth(params.get('deliveryMonthFrom'));
  const deliveryMonthTo = parseMonth(params.get('deliveryMonthTo'));
  const focusMonth = parseMonth(params.get('focusMonth'));

  return {
    bucket,
    season,
    supplierId,
    deliveryMonthFrom,
    deliveryMonthTo,
    focusMonth,
  };
};

export const writePurchaseOrderStatsFilters = (
  baseParams: URLSearchParams,
  state: Partial<PurchaseOrderStatsFilterState>,
): URLSearchParams => {
  const params = new URLSearchParams(baseParams.toString());

  if (state.bucket) params.set('bucket', state.bucket);

  if (state.season !== undefined) {
    if (state.season) params.set('season', state.season);
    else params.delete('season');
  }

  if (state.supplierId !== undefined) {
    if (state.supplierId && state.supplierId > 0)
      params.set('supplierId', String(state.supplierId));
    else params.delete('supplierId');
  }

  if (state.deliveryMonthFrom !== undefined) {
    if (state.deliveryMonthFrom)
      params.set('deliveryMonthFrom', state.deliveryMonthFrom);
    else params.delete('deliveryMonthFrom');
  }
  if (state.deliveryMonthTo !== undefined) {
    if (state.deliveryMonthTo)
      params.set('deliveryMonthTo', state.deliveryMonthTo);
    else params.delete('deliveryMonthTo');
  }

  if (state.focusMonth !== undefined) {
    if (state.focusMonth) params.set('focusMonth', state.focusMonth);
    else params.delete('focusMonth');
  }

  return params;
};

export const readPurchaseOrderListFilters = (
  params: URLSearchParams,
): PurchaseOrderListFilterState => {
  const status = (params.get('status') || '').trim() || null;
  const viskanStatus = (params.get('viskanStatus') || '').trim() || null;

  const readyParam = (params.get('ready') || '').trim();
  const ready =
    readyParam === 'ready' || readyParam === 'not_ready' || readyParam === 'all'
      ? readyParam
      : 'all';

  const season = (params.get('season') || '').trim() || null;

  const supplierIdRaw = params.get('supplierId');
  const supplierIdNum = supplierIdRaw != null ? Number(supplierIdRaw) : NaN;
  const supplierId =
    Number.isFinite(supplierIdNum) && supplierIdNum > 0 ? supplierIdNum : null;

  return {
    status,
    viskanStatus,
    ready,
    season,
    supplierId,
  };
};

export const writePurchaseOrderListFilters = (
  baseParams: URLSearchParams,
  state: Partial<PurchaseOrderListFilterState>,
): URLSearchParams => {
  const params = new URLSearchParams(baseParams.toString());

  if (state.status !== undefined) {
    if (state.status) params.set('status', state.status);
    else params.delete('status');
  }
  if (state.viskanStatus !== undefined) {
    if (state.viskanStatus) params.set('viskanStatus', state.viskanStatus);
    else params.delete('viskanStatus');
  }

  if (state.ready !== undefined) {
    if (state.ready && state.ready !== 'all') params.set('ready', state.ready);
    else params.delete('ready');
  }

  if (state.season !== undefined) {
    if (state.season) params.set('season', state.season);
    else params.delete('season');
  }

  if (state.supplierId !== undefined) {
    if (state.supplierId && state.supplierId > 0)
      params.set('supplierId', String(state.supplierId));
    else params.delete('supplierId');
  }

  return params;
};
